import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import toast from 'react-hot-toast';
import { Model, Queue } from '../../types';
import apiService from '../../services/api';
import CustomSelect from '../common/CustomSelect';

interface QueueCreationModalProps {
  projectId: string;
  isOpen: boolean;
  onClose: () => void;
  onQueueCreated: (queue: Queue) => void;
}

const QueueCreationModal: React.FC<QueueCreationModalProps> = ({
  projectId,
  isOpen,
  onClose,
  onQueueCreated,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedModelId, setSelectedModelId] = useState<string>('');
  const [models, setModels] = useState<Model[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingModels, setLoadingModels] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // AC-460: New queue configuration fields
  const [maxRetryCount, setMaxRetryCount] = useState<number>(3);
  const [singleConsumerMode, setSingleConsumerMode] = useState<boolean>(false);
  // Priority queue support: optional payload field name to use as priority.
  // Empty string = FIFO queue.
  const [priorityField, setPriorityField] = useState<string>('');

  const fetchModels = useCallback(async () => {
    setLoadingModels(true);
    setError(null);
    try {
      const fetchedModels = await apiService.getModels(projectId);
      setModels(fetchedModels);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load models';
      setError(errorMessage);
    } finally {
      setLoadingModels(false);
    }
  }, [projectId]);

  // Fetch models when modal opens or projectId changes
  useEffect(() => {
    if (isOpen) {
      fetchModels();
    }
  }, [isOpen, fetchModels]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setName('');
      setDescription('');
      setSelectedModelId('');
      setError(null);
      // AC-460: Reset new fields to defaults
      setMaxRetryCount(3);
      setSingleConsumerMode(false);
      setPriorityField('');
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Stop event propagation to prevent bubbling to parent forms (AC-427)
    e.stopPropagation();
    setLoading(true);
    setError(null);

    try {
      if (!selectedModel) {
        throw new Error('No model selected');
      }
      // AC-460: Include new configuration fields in queue creation
      const queue = await apiService.createQueue(projectId, {
        name,
        description,
        data_model_id: selectedModel.model_id,
        max_retry_count: maxRetryCount,
        single_consumer_mode: singleConsumerMode,
        priority_field: priorityField.trim() || null,
      });
      toast.success(`Queue "${queue.name}" created`);
      onQueueCreated(queue);
      onClose();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create queue';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const selectedModel = useMemo(() => {
    if (!selectedModelId || models.length === 0) return null;
    return models.find((m) => String(m.model_id) === selectedModelId) || null;
  }, [models, selectedModelId]);

  // Numeric fields the user can pick as the priority. Detected from the
  // selected schema's properties. We look for primitive int/float types -
  // not lists, not nested schemas.
  const numericFields = useMemo(() => {
    if (!selectedModel?.properties) return [];
    return Object.entries(selectedModel.properties)
      .filter(([_, type]) => type === 'int' || type === 'float')
      .map(([name]) => name);
  }, [selectedModel]);

  const isFormValid = name.trim() !== '' && selectedModelId !== '';

  if (!isOpen) return null;

  // Use createPortal to render modal outside the DOM hierarchy (AC-427)
  // This is required because nested HTML forms are invalid - browsers ignore
  // inner <form> tags, causing the outer form to submit instead.
  // By rendering to document.body, we ensure this modal's form is independent.
  const modalContent = (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-cap-dark rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 pb-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-warm-white">Create Queue</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 dark:text-gray-400"
            aria-label="Close"
            data-testid="close-button"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-6 pt-4">
          {error && (
            <div
              className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300"
              data-testid="error-message"
            >
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} autoComplete="off">
            <div className="mb-4">
            <label htmlFor="queue-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Queue Name
            </label>
            <input
              id="queue-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Enter queue name"
              data-testid="queue-name-input"
              required
            />
          </div>

          <div className="mb-4">
            <label htmlFor="queue-description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Description
            </label>
            <textarea
              id="queue-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Enter queue description (optional)"
              rows={3}
              data-testid="queue-description-input"
            />
          </div>

          <div className="mb-4">
            <label htmlFor="data-model" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Data Model
            </label>
            <CustomSelect
              value={selectedModelId}
              onChange={(value) => setSelectedModelId(value)}
              options={models.map((model) => ({
                value: String(model.model_id),
                label: model.class_name,
              }))}
              placeholder="Select a data model"
              aria-label="Data Model"
            />
          </div>

          {/* AC-460: Queue Configuration Options */}
          <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Queue Configuration</h3>

            <div className="mb-3">
              <label htmlFor="max-retry-count" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Max Retry Count
              </label>
              <input
                id="max-retry-count"
                type="number"
                min={1}
                max={10}
                value={maxRetryCount}
                onChange={(e) => setMaxRetryCount(Math.max(1, Math.min(10, parseInt(e.target.value) || 3)))}
                className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                data-testid="max-retry-count-input"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Number of times to retry failed items before moving to error queue (1-10)
              </p>
            </div>

            <div className="flex items-start mb-3">
              <input
                id="single-consumer-mode"
                type="checkbox"
                checked={singleConsumerMode}
                onChange={(e) => setSingleConsumerMode(e.target.checked)}
                className="mt-1 h-4 w-4 text-primary-600 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500"
                data-testid="single-consumer-mode-checkbox"
              />
              <div className="ml-2">
                <label htmlFor="single-consumer-mode" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Single Consumer Mode
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  When enabled, only one workflow can read from this queue at a time
                </p>
              </div>
            </div>

            <div>
              <label htmlFor="priority-field" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Priority Field <span className="text-xs text-gray-500 dark:text-gray-400 font-normal">(optional)</span>
              </label>
              {numericFields.length > 0 ? (
                <CustomSelect
                  value={priorityField}
                  onChange={(value) => setPriorityField(value)}
                  options={[
                    { value: '', label: 'FIFO (no priority)' },
                    ...numericFields.map((name) => ({ value: name, label: name })),
                  ]}
                  placeholder="FIFO (no priority)"
                  aria-label="Priority Field"
                />
              ) : (
                <input
                  id="priority-field"
                  type="text"
                  value={priorityField}
                  onChange={(e) => setPriorityField(e.target.value)}
                  placeholder="e.g. score (leave empty for FIFO)"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  data-testid="priority-field-input"
                />
              )}
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Pick a numeric field from your data model to make this a priority queue. Items
                with higher values are processed first. Leave empty for FIFO ordering.
              </p>
            </div>
          </div>

          {selectedModel && (
            <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg" data-testid="schema-preview">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Schema Preview</h3>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                <div className="mb-2">
                  <span className="font-medium">Model:</span> {selectedModel.class_name}
                </div>
                {selectedModel.description && (
                  <div className="mb-2">
                    <span className="font-medium">Description:</span> {selectedModel.description}
                  </div>
                )}
                <div>
                  <span className="font-medium">Properties:</span>
                  <ul className="ml-4 mt-1 space-y-1">
                    {Object.entries(selectedModel.properties).map(([key, value]) => {
                      // AC-1251: properties values may be either a string shorthand or a rich object.
                      const typeStr = typeof value === 'string' ? value : String((value as any)?.type ?? '');
                      return (
                        <li key={key}>
                          <span className="font-mono text-xs">{key}</span>: <span className="text-gray-500 dark:text-gray-400">{typeStr}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {loading && (
            <div className="mb-4 flex items-center justify-center" data-testid="loading-indicator">
              <div className="text-sm text-gray-600 dark:text-gray-400">Creating queue...</div>
            </div>
          )}

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-cap-dark border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              data-testid="cancel-button"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!isFormValid || loading}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="create-button"
              aria-label="Create Queue"
            >
              {loading ? 'Creating...' : 'Create'}
            </button>
          </div>
          </form>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default QueueCreationModal;
